/**
 * Bottone «Condividi» della scheda ticket (admin e rappresentante, entrambe le
 * vesti): menu con «Copia link», «Condividi su WhatsApp» e, se il ticket ha il
 * thread caricato, «Condividi via email» (modale `v4/ShareEmailModal`). Il link e quello
 * universale `/t/<numero>` (nessun token: apre il ticket solo a chi ne ha
 * accesso con la propria sessione). Con `busy` resta visibile ma inerte come
 * il resto della barra azioni (niente «ballo», CT-68).
 * Con `print` (adapter operatore) c'e anche «Stampa» (modale `v4/PrintTicketModal`:
 * PDF o stampante, allegati, note interne a chi le vede).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link2, Mail, MessageCircle, Printer, Share2 } from 'lucide-react';
import { S3Menu, S3MenuItem } from './v3/primitives';
import { useToast } from '../../contexts/ToastContext';
import { copyToClipboard, ticketShareUrl, whatsappShareHref } from './shareTicket';
import { isLimitedViewer } from './v3/actionsModel';
import ShareEmailModal from './v4/ShareEmailModal';
import PrintTicketModal from './v4/PrintTicketModal';
import type { ShareEmailTicket } from './v4/shareEmailModel';
import type { ShareLinksPayload, ShareLinksResult, TicketPrintFile, TicketPrintOptions } from '../../services/supportApi';

/** «Stampa» del ticket: PDF dal server e visibilita delle note per l'utente. */
export interface TicketPrintConfig {
  printTicket: (options: TicketPrintOptions) => Promise<TicketPrintFile>;
  /** L'utente vede le note interne nella scheda (admin: `supporttickets.handle`). */
  notesVisible: boolean;
}

export interface ShareTicketButtonProps {
  /** Con `publicThread` (ticket caricato nella scheda) compare anche «Condividi via email». */
  ticket: ShareEmailTicket;
  /** Classe in piu sulla pill (v4: `s4-hero__btn`). */
  className?: string;
  /** Barra azioni inerte: aria-disabled, click a vuoto. */
  busy?: boolean;
  /** Link pubblici firmati agli allegati per «Condividi via email» (adapter operatore). */
  createShareLinks?: (payload: ShareLinksPayload) => Promise<ShareLinksResult>;
  /** «Stampa» (adapter operatore). Assente = nessuna voce. */
  print?: TicketPrintConfig;
}

/**
 * Toast se c'e il provider (app); senza (test, montaggi isolati) undefined e la
 * conferma passa da una regione role="status". useContext gira sempre: l'ordine
 * degli hook non cambia.
 */
const useToastIfAvailable = (): ReturnType<typeof useToast> | undefined => {
  try {
    return useToast();
  } catch {
    return undefined;
  }
};

const ShareTicketButton: React.FC<ShareTicketButtonProps> = ({ ticket, className, busy, createShareLinks, print }) => {
  const toast = useToastIfAvailable();
  const [open, setOpen] = useState(false);
  // Ripiego: appunti non disponibili → il link resta selezionato in un campo da copiare a mano.
  const [manual, setManual] = useState(false);
  const [copiedNote, setCopiedNote] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  // Il partecipante invitato (e il canale in copia, parte A/B) non condivide il
  // ticket via email: restano «Copia link» e WhatsApp.
  const canEmail = Array.isArray(ticket.publicThread) && !isLimitedViewer(ticket.viewerRole);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const url = ticketShareUrl(origin, ticket);

  const close = useCallback(() => {
    setOpen(false);
    setManual(false);
    triggerRef.current?.focus({ preventScroll: true });
  }, []);

  // La barra diventa inerte mentre il menu e aperto: il menu si chiude.
  useEffect(() => {
    if (!busy) return;
    setOpen(false);
    setManual(false);
  }, [busy]);

  const copy = async () => {
    const ok = await copyToClipboard(url);
    if (!ok) {
      setManual(true);
      return;
    }
    close();
    if (toast) toast.success('Link copiato');
    else setCopiedNote(true);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`s3-pill${open ? ' s3-pill--active' : ''}${className ? ` ${className}` : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-disabled={busy || undefined}
        title="Condividi il link al ticket"
        onClick={() => {
          if (busy) return;
          setCopiedNote(false);
          setManual(false);
          setOpen((v) => !v);
        }}
      >
        <Share2 size={13} aria-hidden="true" /> Condividi
      </button>
      <S3Menu open={open} onClose={close} ariaLabel="Condividi" triggerRef={triggerRef}>
        <S3MenuItem onClick={() => void copy()}>
          <Link2 size={13} aria-hidden="true" /> Copia link
        </S3MenuItem>
        <a
          role="menuitem"
          className="s3-menuitem"
          href={whatsappShareHref(origin, ticket)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => close()}
        >
          <MessageCircle size={13} aria-hidden="true" /> Condividi su WhatsApp
        </a>
        {canEmail && (
          <S3MenuItem
            onClick={() => {
              close();
              setEmailOpen(true);
            }}
          >
            <Mail size={13} aria-hidden="true" /> Condividi via email
          </S3MenuItem>
        )}
        {print && (
          <S3MenuItem
            onClick={() => {
              close();
              setPrintOpen(true);
            }}
          >
            <Printer size={13} aria-hidden="true" /> Stampa
          </S3MenuItem>
        )}
        {manual && (
          // Fuori dalla navigazione del menu: non e una voce, e i tasti
          // (frecce, Home/End) dentro il campo restano al campo. Escape chiude
          // comunque il menu (useEscape ascolta sul documento).
          <div role="none" style={{ padding: '6px 8px' }} onKeyDown={(e) => e.stopPropagation()}>
            <label style={{ display: 'block', fontSize: 11, marginBottom: 4 }}>
              Copia non disponibile: seleziona e copia il link
              <input
                type="text"
                readOnly
                value={url}
                className="s3-search s3-search--plain"
                style={{ marginTop: 4, width: '100%' }}
                onFocus={(e) => e.currentTarget.select()}
                autoFocus
              />
            </label>
          </div>
        )}
      </S3Menu>
      {emailOpen && (
        <ShareEmailModal ticket={ticket} onClose={() => setEmailOpen(false)} createShareLinks={createShareLinks} />
      )}
      {printOpen && print && (
        <PrintTicketModal
          ticket={ticket}
          onClose={() => setPrintOpen(false)}
          printTicket={print.printTicket}
          notesVisible={print.notesVisible}
        />
      )}
      {copiedNote && (
        <span role="status" className="s3-sr-only">
          Link copiato
        </span>
      )}
    </>
  );
};

export default ShareTicketButton;
